import './locales/register';
import { createApp, shallowReactive, watch } from 'vue';
import { createPinia } from 'pinia';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { fr, en } from 'vuetify/locale'
import { App, getRouter, vuetifyOptions, registerConfigurationPanel, type IConfiguration, type IProfileTab } from '@fondationcss/ui';
import { getLanguage, translate, useSdk } from '@fondationcss/sdk';
import { getCustomRoutes } from './routes';
import { menus } from './sidebar-items';
import SystemAdminTab from './components/SystemAdminTab.vue';
import InscriptionLoginButton from './components/inscription-login-button.vue';
import LoginInscriptionPanel from './components/login-inscription-panel.vue';
import GricsApiPanel from './components/GricsApiPanel.vue';
import { loginExpanded } from './composables/useLoginInscription';
import { WikiPlugin } from '@fondationcss/wiki'
import { FormPlugin } from '@fondationcss/forms'

import 'vuetify/styles';
import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';
import '@fondationcss/ui/dist/style.css';
import '@fondationcss/wiki/dist/style.css';
import '@fondationcss/forms/dist/style.css';
import '@fondationcss/markdown/dist/markdown.css';
import '@fondationcss/grics/dist/index.css';
// The shared components' own styles (@byrnu/agora-shared): the locked switches of the catalog tabs,
// the centre management screen.
import '@byrnu/agora-shared/index.css';
import "@/styles.css";
import 'highlight.js/styles/github.css';

// The GRICS gateway diagnostics belong next to the settings they exercise: the configuration page
// already edits Grics:Authority and friends, but nothing there proves the values actually work.
// Registered rather than imported by the page, because @fondationcss/ui must not depend on an
// opt-in satellite — see configuration-tabs.ts (mirrors fondation.example's own registration).
registerConfigurationPanel({
  value: 'grics-api',
  get title() { return translate('perspective.app.grics-api'); },
  icon: 'fas fa-plug',
  parent: 'grics',
  component: GricsApiPanel,
});


const config = {
  AppName: 'Byrnu Perspective',
  SiteNameShort: 'Byrnu Perspective',
  SiteNameLong: 'Byrnu Perspective',
  SiteLogo: "/perspective-logo.svg",
  SiteLogoAlt: '/perspective-logo-white.svg',
  LogoLine1: 'Byrnu',
  LogoLine2: 'Perspective',
  SidebarItems: [],
  Menus: menus,
  MenuGroupsOrientation: 'vertical',
  Logo: '/byrnu-logo.svg',
  Copyright: `© ${new Date().getFullYear()} Byrnu inc.`,
  CopyrightUrl: 'https://byrnu.com/?utm_source=byrnu-perspective',
  DefaultLayout: 'byrnu-red-app',
  ThemeColor: '#3460a8',
  // L'éditeur de calendrier Fondation délègue la préparation du PDF à l'espace Rapports du
  // produit : son bouton « Imprimer en PDF » pointe cette route, en y ajoutant `calendarId` et
  // `calendarYearId`. Sans cette clé, le bouton disparaît de la page calendrier.
  CalendarReportRoute: '/admin/rapports?type=calendar',
} as IConfiguration;

const vuetify = createVuetify({
  components,
  directives,
  ...vuetifyOptions,
  locale: {
    // Vuetify's own texts (pagination, data tables…) follow the application language.
    locale: getLanguage() === 'en-CA' ? 'en' : 'fr',
    messages: { fr, en }
  }
})
watch(getLanguage, (language) => {
  vuetify.locale.current.value = language === 'en-CA' ? 'en' : 'fr';
});
const app = createApp(App);
// Bouton « Nouvelle inscription » rendu sous « Se connecter » via le point
// d'injection Fondation « loginExtra » (dans le panneau de login).
app.provide('loginExtra', InscriptionLoginButton);
// Mode étendu de l'écran de connexion : le bouton bascule `loginExpanded`, la
// Fondation rétrécit le col image de gauche et rend ce panneau dans les 3/4 droite.
app.provide('loginExpanded', loginExpanded);
app.provide('loginExpandedComponent', LoginInscriptionPanel);
import Gantt from "@fondationcss/gantt";
app.use(Gantt); // default export
const router = getRouter(getCustomRoutes as () => []);

// Masquer le pied de page sur toutes les pages (le layout Fondation lit
// route.meta.hideFooter). On l'applique à toutes les routes existantes et à
// toute navigation (couvre aussi les routes intégrées de la Fondation).
router.getRoutes().forEach((r) => { (r.meta ??= {}).hideFooter = true; });
router.beforeEach((to) => { to.meta.hideFooter = true; });

// Restriction candidat : un utilisateur authentifié via CourierID (applicant) ne voit
// QUE la page d'inscription. Détecté via le fournisseur d'auth de la session (autoritatif,
// survit au rechargement). Fail-open : en cas d'échec, on ne restreint pas.
let applicantChecked = false;
let sessionIsApplicant = false;
router.beforeEach(async (to) => {
  if (to.path === '/inscription') return true;
  if (!applicantChecked) {
    try {
      const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
      const session = await fetch(`${base}/core/auth/session`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null));
      const provider = (session?.authProvider ?? '').toString().toLowerCase();
      sessionIsApplicant = provider.includes('courierid');
      applicantChecked = true;
    } catch {
      applicantChecked = true;
    }
  }
  return sessionIsApplicant ? '/inscription' : true;
});

app.provide('config', config)
app.provide('sdkOptions', { apiUrl: import.meta.env.VITE_API_URL })

// Section cachée « Administration système » : onglet injecté dans le popup de
// profil, visible uniquement pour les administrateurs. On garde la liste des
// onglets synchronisée avec le rôle courant (sdk.me est chargé de façon
// asynchrone après le bootstrap), de sorte que l'onglet n'apparaît jamais pour
// les non-administrateurs.
const systemAdminTab: IProfileTab = {
  value: 'perspective-system-admin',
  icon: 'fas fa-user-gear',
  get label() { return translate('perspective.app.system-admin-tab'); },
  component: SystemAdminTab,
  insertLocation: '222', // insère avant l'onglet « Paramètres »
};
const profileTabs = shallowReactive<IProfileTab[]>([]);
const sdk = useSdk();
watch(
  () => sdk.me.value?.permissions,
  (permissions) => {
    // L'onglet ne mène qu'à la page des admissions : aligné sur la permission backend
    // des routes Admissions (Reviewer). L'ancien « Perspective.Manage » n'existe plus.
    const isAdmin = permissions?.includes('Perspective.Admissions.Reviewer') ?? false;
    const index = profileTabs.findIndex(t => t.value === systemAdminTab.value);
    if (isAdmin && index === -1) {
      profileTabs.push(systemAdminTab);
    } else if (!isAdmin && index !== -1) {
      profileTabs.splice(index, 1);
    }
  },
  { immediate: true }
);
app.provide('profileTabs', profileTabs);
app.use(vuetify);
app.component('font-awesome-icon', FontAwesomeIcon)
library.add(fas)
library.add(far)
app.use(createPinia());
app.use(PerfectScrollbarPlugin);
app.use(router);
app.use(WikiPlugin, { router })
app.use(FormPlugin)
app.mount('#app');

// Désactive les suggestions d'autofill du navigateur sur tous les champs :
// les formulaires Arkiv ne contiennent pas de données personnelles et les
// suggestions de Chrome y sont toujours hors contexte. Le `defaults` de Vuetify
// ne peut pas le faire (il n'applique que les props déclarées, et `autocomplete`
// est un attribut passé à l'input), d'où l'application au niveau du DOM.
// Un champ peut se soustraire en déclarant lui-même un attribut autocomplete.
new MutationObserver(() => {
  document
    .querySelectorAll('input:not([autocomplete]), textarea:not([autocomplete])')
    .forEach((el) => el.setAttribute('autocomplete', 'off'));
}).observe(document.body, { childList: true, subtree: true });
